import { ThemeProvider } from "@mui/material/styles";
import { GlobalStoreProvider } from "./store";
import theme from "./theme";
import Router from "./router";
import "./services/i18n";
import { initialiseApplicationMonitoring } from "./services/application-monitoring-adapter";
import {
  trackAppDistribution,
  trackBinahSDKVersion,
  trackDeviceInfo,
  trackEvent,
} from "./services/analytics-adapter";
import {
  getAppDistributionIdentifier,
  getDefaultFontSize,
  getUserAgent,
} from "./utils";
import { BrowserRouter } from "react-router-dom";
import { SnackbarProvider } from "./context/SnackbarProvider";
import DefaultSnackbar from "./features/Common/components/DefaultSnackbar";
import { ErrorBoundary } from "react-error-boundary";
import ErrorFallbackWithReload from "./features/Common/components/ErrorFallbackWithReload";
import { QueryClientProvider } from "@tanstack/react-query";
import { sharedQueryClient } from "./query-client";
import {
  FeatureToggleIdentifier,
  isFeatureEnabled,
} from "./services/feature-toggle-adapter";
import { persistValue, retrieveValue } from "@/store/persistence";
import { InsightsProvider } from "./insights/InsightsProvider";
import { NativeBridgeProvider } from "./bridge/NativeBridgeProvider";
import { NotificationProvider } from "./bridge/NotificationProvider";

function setAndGetThirdPartyMember(thirdParty: string) {
  const isThirdPartyMemberLocalStorageValue = retrieveValue(
    "isUserThirdPartyMember"
  );

  if (isThirdPartyMemberLocalStorageValue === true) {
    return true;
  }

  const urlParts = window.location.toString().split("?");
  const keyValuePair = urlParts.find((pair) =>
    pair.startsWith(`${thirdParty}=`)
  );

  const isThirdParty = keyValuePair?.split("=")[1];

  if (
    isThirdParty?.toLowerCase() === "true" &&
    isFeatureEnabled(FeatureToggleIdentifier.ENABLE_THIRD_PARTY_AUTHENTICATION)
  ) {
    persistValue("isUserThirdPartyMember", true);
    return true;
  }

  return false;
}

const appDistributionIdentifier = getAppDistributionIdentifier(
  window.location.toString()
);

initialiseApplicationMonitoring();
trackAppDistribution(appDistributionIdentifier);
trackDeviceInfo();
trackBinahSDKVersion();

trackEvent({
  event: "action.applicationLaunched",
  userAgent: getUserAgent(),
  "user.browserFontSize": getDefaultFontSize(),
});

export default function App() {
  return (
    <QueryClientProvider client={sharedQueryClient}>
      <ThemeProvider theme={theme}>
        <ErrorBoundary FallbackComponent={ErrorFallbackWithReload}>
          <InsightsProvider />
          <NativeBridgeProvider />
          <GlobalStoreProvider
            appDistributionIdentifier={appDistributionIdentifier}
            isThirdPartyMember={setAndGetThirdPartyMember("churchOfUganda")}
          >
            <SnackbarProvider>
              <NotificationProvider />
              <DefaultSnackbar />
              <BrowserRouter>
                <Router />
              </BrowserRouter>
            </SnackbarProvider>
          </GlobalStoreProvider>
        </ErrorBoundary>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
